import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  gql,
  useQuery,
  useNavigate,
  useLocation,
  useParams,
} from '@common/hooks';
import { useCurrentMembership, useCurrentEntity } from '@identity/hooks';
import { Colors, Spacing } from 'works/legacy/lib/style-helper';
import { ExtendOffer } from 'works/components/extension-offer/extend-offer';
import {
  Card,
  ErrorHandler,
  Icon,
  LoadingState,
  Tabs,
} from '@common/components';
import { AssignmentStateMenu } from 'works/components/assignment-state-menu';
import { ExpiredComplianceItemsCount } from 'works/components/expired-compliance-items-count';
import { ExtensionOfferState } from 'works/components/extension-offer-state/extension-offer-state';
import { AssignmentNotes } from '@working-experience/components';
import { OrganizationOnly } from 'works/components/auth-guard/organization-only';
import { CandidateProfile } from 'works/components/candidate-profile';
import { AssignmentPane } from './assignment-pane';
import { AssignmentDetails } from './assignment-details';
import { CandidateAssignmentComplianceTab } from './candidate-assignment-compliance-tab';
import { CandidateAssignmentInfoTable } from './candidate-assignment-info-table';
import { CandidateAssignmentHeader } from './candidate-assignment-header';
import { pluralizeWeeksValue } from 'works/legacy/services/length-in-weeks-service';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { CommentInfoFragment, HistoryInfoFragment } from '@common/graphql';
import { ComplianceDeadlineInfoBanner } from '@onboarding/components';
import { OFFER_INFO_FRAGMENT } from '@applicant-flow/graphql';
import { CANDIDATE_ASSIGNMENT_COMPLEMENT_FRAGMENT } from '@working-experience/graphql';

const AssignmentStateMetaFragment = gql`
  fragment AssignmentStateMetaFragment on StaffingRequestAssignment {
    id
    state
    startDate
    endDate
    finalEndDate
    stateChangedAt

    worksList {
      id
      complianceDeadlineDetails
    }
  }
`;

const CANDIDATE_ASSIGNMENT_QUERY = gql`
  query CandidateAssignmentQuery($id: ID!) {
    staffingRequestAssignment(id: $id) {
      id
      state
      shiftType
      totalLengthInWeeks
      originalStartDate
      startDate
      finalEndDate
      neverStarted
      timeoffDescription
      extensionCount
      cancelledDetail
      doNotRehire
      canUpdate {
        value
      }
      externalIds {
        id
        identifier
        createdAt
      }
      organizationUnit {
        id
        name
        costCenter
        organizationBusiness {
          id
          name
          city
          stateCode
        }
      }
      hiringLeader {
        id
        name
      }
      profileStatusHistory {
        ...HistoryInfoFragment
      }
      worker {
        id
        worksUser {
          id
          name
        }
      }
      comments {
        ...CommentInfoFragment
      }
      worksProfile {
        id
        fullName
        phone
        email
        identificationCode
        canShowPhi {
          value
        }
        ...CandidateProfileWorksProfileFragment
      }
      offer {
        id
        createdAt
        ...OfferBodyInfo
      }
      lastAssignmentTerm {
        id
        overtimeThreshold
        organizationUnit {
          id
        }
      }
      notVoidedSourceAssignmentTerms {
        id
        upcoming
        past
        currentlyActive
        effectiveAt
        startDate
        endDate
        estimatedBudget
        lengthInWeeks
        billingRegularRate
        shiftsPerWeek
        hoursPerShift
        shiftType
        timeoffDescription
        createdByName
        updatedFields
        irrelevant
        updatedAt
        createdAt
        overtimeThreshold
        organizationUnit {
          id
          name
          costCenter
        }
        hiringLeader {
          id
          name
        }
        canVoid {
          value
        }
        canUpdate {
          value
        }
      }
      canExtend {
        value
      }
      staffingRequest {
        id
        name
        role {
          id
          name
        }
        hiringLeader {
          id
          name
        }
        ...CandidateAssignmentInfoTableStaffingRequestFragment
      }
      supplier {
        id
        name
      }
      organizationUnit {
        id
        name
        costCenter
        department {
          id
          name
        }
        approvalChainTemplates {
          id
        }
      }
      organizationBusiness {
        id
        name
        city
        stateCode
      }
      organization {
        id
        name
      }
      organizationProfile {
        id
        employeeId
        estimatedConversionDate
        contractHoursRemaining
        conversionDate
        estimatedConversionDate
        arrivedAtDate
        estimatedArrivalDate
        canUpdate {
          value
        }
        canUpdateArrivalDates {
          value
        }
        worksProfile {
          id
          isInternational
        }
      }
      extensionOffers {
        id
        state
        lastTermCreatedTillExtensionOfferCreation {
          id
          organizationUnit {
            id
            department {
              id
              name
              organizationBusiness {
                id
                name
                city
                stateCode
              }
            }
          }
        }
        history {
          ...HistoryInfoFragment
        }
        notVoidedSourceAssignmentTerms {
          id
          upcoming
          past
          currentlyActive
          effectiveAt
          startDate
          endDate
          lengthInWeeks
          billingRegularRate
          shiftsPerWeek
          hoursPerShift
          shiftType
          timeoffDescription
          irrelevant
          createdByName
          updatedFields
          updatedAt
          createdAt
          overtimeThreshold
        }
      }
      worksList {
        id
        deadlineDueDate
        deadlineDetails
        worksListTemplate {
          id
          name
          complianceDeadlineDetails
        }
      }
      ...AssignmentStateMenuFragment
      ...AssignmentStateMetaFragment
      ...ExpiredComplianceItemsCountFragment
      ...ExtendOfferFragment
      ...CandidateAssignmentComplementFragment
      ...ExtensionOfferStateFragment
      ...AssignmentPaneStaffingRequestAssignmentFragment
    }
  }
  ${AssignmentStateMenu.fragment}
  ${AssignmentStateMetaFragment}
  ${AssignmentPane.fragments.staffingRequestAssignment}
  ${CandidateAssignmentInfoTable.fragments.staffingRequest}
  ${CandidateProfile.fragments.worksProfile}
  ${ExpiredComplianceItemsCount.fragment}
  ${ExtendOffer.fragment}
  ${ExtensionOfferState.fragment}
  ${OFFER_INFO_FRAGMENT}
  ${HistoryInfoFragment}
  ${CommentInfoFragment}
  ${CANDIDATE_ASSIGNMENT_COMPLEMENT_FRAGMENT}
`;

export function CandidateAssignment({ staffingRequestAssignmentId }) {
  const navigate = useNavigate();
  const location = useLocation();

  const currentMembership = useCurrentMembership();
  const currentEntity = useCurrentEntity();
  const { type, id, tabKey, resourceId, fixMeTabKey } = useParams();

  const { loading, error, data } = useQuery(CANDIDATE_ASSIGNMENT_QUERY, {
    fetchPolicy: 'cache-network',
    variables: {
      id: staffingRequestAssignmentId,
    },
  });

  const anyError = error;

  if (anyError) {
    return <ErrorHandler error={anyError} log="CandidateAssignment" />;
  }

  if (loading || !data) {
    return <LoadingState />;
  }

  const drawerUrlSource = location.pathname.split('/')[2];

  const staffingRequestAssignment = data?.staffingRequestAssignment;
  const worksProfileId = staffingRequestAssignment?.worksProfile?.id;

  const templateDeadlineDetails =
    data?.staffingRequestAssignment?.worksList?.worksListTemplate
      ?.complianceDeadlineDetails;

  const overrideDeadlineDetails =
    data?.staffingRequestAssignment?.worksList?.deadlineDetails;

  const complianceDeadlineDueDate =
    data?.staffingRequestAssignment?.worksList?.deadlineDueDate;

  const isOnboardingState =
    data?.staffingRequestAssignment.state === 'onboarding';

  const canSeeOnboardingTab = currentMembership.canShowSidebarOnboardingItem;
  const canSeeNotesTab = currentMembership.canShowLongTermAssignmentComments;

  if (type === 'on_demand' && id && resourceId) {
    setBreadcrumbs([
      { name: 'Team', path: '/works/team' },
      { name: 'Worker', path: '/works/team/on_demand' },
      {
        name: staffingRequestAssignment?.worker?.worksUser?.name,
        path: `/works/team/on_demand/${id}`,
        privateInfo: true,
      },
      {
        name: 'Assignments',
        path: `/works/team/on_demand/${id}/assignments`,
      },
      {
        name: staffingRequestAssignment?.worksProfile?.fullName,
        path: `/works/team/on_demand/${id}/assignments/${resourceId}`,
        privateInfo: true,
      },
    ]);
  } else {
    const breadcrumbTitle =
      drawerUrlSource === 'team'
        ? { name: 'Team', path: '/works/team' }
        : { name: 'Onboarding', path: '/works/onboarding' };
    setBreadcrumbs([
      breadcrumbTitle,
      { name: 'Assignment', path: breadcrumbTitle.path },
      {
        name: staffingRequestAssignment?.worksProfile?.fullName,
        path: `/works/${drawerUrlSource}/long_term/${staffingRequestAssignmentId}`,
        privateInfo: true,
      },
    ]);
  }

  function buildAddress({ name, city, stateCode }) {
    return `${name} - ${city}, ${stateCode}`;
  }

  const commentsCount = staffingRequestAssignment.comments.length;
  const commentsCountText = commentsCount ? `(${commentsCount})` : '';

  return (
    <>
      <CandidateAssignmentHeader assignment={staffingRequestAssignment} />
      <section className="CandidateAssignment">
        <div className="assignment-details">
          <AssignmentDetails icon={<Icon icon="mapMarkerAlt" kind="light" />}>
            <div className="text-heavy">
              {staffingRequestAssignment.organization.name}
            </div>
            <div className="text-heavy">
              {staffingRequestAssignment.organizationUnit.name}
            </div>
            <div>
              {buildAddress(
                staffingRequestAssignment.organizationUnit.organizationBusiness
              )}
            </div>
          </AssignmentDetails>

          <AssignmentDetails icon={<Icon icon="calendarAlt" kind="light" />}>
            <div className="text-heavy">
              {moment(staffingRequestAssignment.originalStartDate).format('L')}{' '}
              -{' '}
              <span
                className={
                  staffingRequestAssignment.state === 'cancelled'
                    ? 'color-coral'
                    : ''
                }
              >
                {moment(staffingRequestAssignment.finalEndDate).format('L')}
              </span>
            </div>
            <div>
              Total length:{' '}
              <span className="text-heavy">
                {pluralizeWeeksValue(
                  staffingRequestAssignment.totalLengthInWeeks
                )}
              </span>
            </div>
            <div>
              Extended:{' '}
              <span className="text-heavy">
                {staffingRequestAssignment.extensionOffers &&
                  staffingRequestAssignment.extensionOffers.length}
              </span>
            </div>
          </AssignmentDetails>

          <AssignmentDetails icon={<Icon icon="calendarAlt" kind="light" />}>
            <>
              <div className="text-heavy">
                {staffingRequestAssignment.staffingRequest.role.name} -{' '}
                {staffingRequestAssignment.shiftType}
              </div>
              <OrganizationOnly>
                <div className="sentry-mask">
                  HL:{' '}
                  <span className="text-heavy">
                    {staffingRequestAssignment.hiringLeader.name}
                  </span>
                </div>
                <div className="sentry-mask">
                  CC:{' '}
                  <span className="text-heavy">
                    {staffingRequestAssignment.organizationUnit.costCenter}
                  </span>
                </div>
              </OrganizationOnly>
            </>
          </AssignmentDetails>
        </div>

        <div>
          <Tabs
            defaultActiveKey={fixMeTabKey || tabKey || 'assignment'}
            onTabClick={(key) => {
              const typePart = type ? `/${type}` : '';

              if (resourceId && type === 'on_demand') {
                navigate(
                  `/works/${drawerUrlSource}${typePart}/${id}/${tabKey}/${resourceId}/${key}`,
                  { replace: true }
                );
              } else {
                navigate(
                  `/works/${drawerUrlSource}${typePart}/${staffingRequestAssignmentId}/${key}`,
                  { replace: true }
                );
              }
            }}
          >
            <Tabs.Pane tab="assignment" key="assignment">
              <AssignmentPane
                assignment={staffingRequestAssignment}
                key={`AssignmentPane-${staffingRequestAssignment.id}`}
              />
            </Tabs.Pane>
            {canSeeOnboardingTab && (
              <Tabs.Pane tab="compliance" key="compliance">
                <CandidateAssignmentComplianceTab
                  worksListId={staffingRequestAssignment.worksList.id}
                  bannerSlot={
                    isOnboardingState && (
                      <ComplianceDeadlineInfoBanner
                        deadlineDetails={
                          overrideDeadlineDetails || templateDeadlineDetails
                        }
                        deadlineDueDate={complianceDeadlineDueDate}
                      />
                    )
                  }
                />
              </Tabs.Pane>
            )}
            <Tabs.Pane tab="info" key="info">
              <Card className="info-tab">
                <CandidateAssignmentInfoTable
                  staffingRequestAssignment={staffingRequestAssignment}
                  worksList={staffingRequestAssignment.worksList}
                />
              </Card>
            </Tabs.Pane>
            <Tabs.Pane tab="profile" key="profile">
              <Card className="profile-tab">
                <CandidateProfile
                  worksProfile={staffingRequestAssignment.worksProfile}
                />
              </Card>
            </Tabs.Pane>
            {canSeeNotesTab && (
              <Tabs.Pane tab={`notes ${commentsCountText}`} key="notes">
                <AssignmentNotes
                  className="notes-tab"
                  comments={staffingRequestAssignment.comments}
                  currentEntityType={currentEntity.entityType}
                  staffingRequestApplicationId={
                    staffingRequestAssignment.staffingRequestApplication.id
                  }
                  worksProfileId={worksProfileId}
                  staffingRequestAssignmentId={staffingRequestAssignment.id}
                  preventNewNotes={
                    staffingRequestAssignment.state === 'cancelled'
                  }
                />
              </Tabs.Pane>
            )}
          </Tabs>
        </div>
      </section>
      <style jsx>{`
        .assignment-details {
          display: flex;
          margin-bottom: ${Spacing.baseSpacingPx};
        }
        :global(.CandidateAssignment .compliance-section) {
          margin-top: ${Spacing.baseSpacing}px;
          padding-top: ${Spacing.baseSpacing / 2}px;
          border-top: 1px solid ${Colors.steel};
        }
        :global(.CandidateAssignment .compliance-section:empty) {
          border-top: 0;
        }
        :global(.CandidateAssignment .profile-tab),
        :global(.CandidateAssignment .info-tab),
        :global(.CandidateAssignment .notes-tab) {
          margin-top: ${Spacing.baseSpacing / 2}px;
        }
      `}</style>
    </>
  );
}

CandidateAssignment.propTypes = {
  staffingRequestAssignmentId: PropTypes.string.isRequired,
  drawerUrlSource: PropTypes.string,
};
